// Conferência das contas e pendências do fechamento do mês. Puro (sem React,
// sem Supabase), usado pela aba Balancete e pelo assistente Fechamento do mês.

export const CODIGO_A_CLASSIFICAR = '9.9.9';

const centavos = (v) => Math.round(Number(v || 0) * 100);

// Checagens automáticas por linha do balancete (antes inline na aba Balancete).
export function flagConta(l) {
  if (!l) return null;
  const saldo = l.saldoFinal || 0;
  if (l.codigo === CODIGO_A_CLASSIFICAR) return { tipo: 'erro', msg: 'Categoria sem conta contábil mapeada' };
  // Ativo: saldo positivo = devedor (normal). Negativo = credor (incomum)
  if (l.tipo === 'A' && saldo < -0.01) return { tipo: 'alerta', msg: 'Conta de Ativo com saldo credor (incomum)' };
  // Passivo: saldo positivo = credor (normal). Negativo = devedor (incomum)
  if (l.tipo === 'P' && saldo < -0.01) return { tipo: 'alerta', msg: 'Conta de Passivo com saldo devedor (incomum)' };
  // Despesa: saldo positivo = devedor (normal). Negativo = credor (incomum)
  if ((l.tipo === 'D' || l.tipo === 'DR') && saldo < -0.01) return { tipo: 'info', msg: 'Despesa com saldo credor — verifique estorno' };
  // Receita: saldo positivo = credor (normal). Negativo = devedor (incomum)
  if (l.tipo === 'R' && saldo < -0.01) return { tipo: 'info', msg: 'Receita com saldo devedor — verifique estorno' };
  return null;
}

// Foto dos números da conta no momento da validação (gravada em contabil_validacoes.totais).
export function totaisDaLinha(l) {
  return {
    saldoAnterior: centavos(l && l.saldoAnterior) / 100,
    debitos: centavos(l && l.debitos) / 100,
    creditos: centavos(l && l.creditos) / 100,
    saldoFinal: centavos(l && l.saldoFinal) / 100,
  };
}

export function totaisIguais(a, b) {
  if (!a || !b) return false;
  return ['saldoAnterior', 'debitos', 'creditos', 'saldoFinal'].every(k => centavos(a[k]) === centavos(b[k]));
}

/**
 * Situação da conta no checklist do mês.
 *  - 'pendente'      sem validação, ou validação desfeita
 *  - 'desatualizada' validada, mas algum lançamento do banco da conta no mês foi
 *                    criado/alterado depois da validação, ou os números da conta
 *                    não batem mais com os gravados na validação
 *  - 'validada'
 *
 * @param {object} linha        linha do balancete mensal
 * @param {object} validacao    registro de contabil_validacoes (ou undefined)
 * @param {Array}  lancsConta   balancete.lancamentosPorConta[codigo]
 */
export function situacaoValidacao(linha, validacao, lancsConta = []) {
  if (!validacao || validacao.status !== 'validado') return 'pendente';
  const validadoEm = Date.parse(validacao.validado_em || '');
  if (Number.isFinite(validadoEm)) {
    const mudou = (lancsConta || []).some(lc => {
      const t = Date.parse(lc.alteradoEm || '');
      return Number.isFinite(t) && t > validadoEm;
    });
    if (mudou) return 'desatualizada';
  }
  if (validacao.totais && linha && !totaisIguais(validacao.totais, totaisDaLinha(linha))) return 'desatualizada';
  return 'validada';
}

// Soma do detalhe da conta, para conferir com a linha do balancete.
export function conferirDetalhe(linha, lancsConta = []) {
  const deb = (lancsConta || []).filter(l => l.lado === 'D').reduce((s, l) => s + centavos(l.valor), 0);
  const cred = (lancsConta || []).filter(l => l.lado === 'C').reduce((s, l) => s + centavos(l.valor), 0);
  const difDeb = deb - centavos(linha && linha.debitos);
  const difCred = cred - centavos(linha && linha.creditos);
  return {
    debitos: deb / 100,
    creditos: cred / 100,
    diferencaDebitos: difDeb / 100,
    diferencaCreditos: difCred / 100,
    confere: difDeb === 0 && difCred === 0,
  };
}

// Contas que entram no checklist: todas as do balancete do mês, menos a 9.9.9
// (ela não se valida — se classifica na etapa 2). Ordem por código, numérica.
export function contasParaConferir(linhas = []) {
  return (linhas || [])
    .filter(l => l.codigo !== CODIGO_A_CLASSIFICAR)
    .slice()
    .sort((a, b) => String(a.codigo).localeCompare(String(b.codigo), 'pt-BR', { numeric: true }));
}

// Próxima conta não validada depois da atual (dá a volta na lista).
// `itens` = [{ codigo, situacao }]. Devolve o código ou null se não houver.
export function proximaPendente(itens = [], codigoAtual) {
  const n = itens.length;
  if (!n) return null;
  const i = itens.findIndex(x => x.codigo === codigoAtual);
  for (let k = 1; k <= n; k++) {
    const x = itens[(i + k + n) % n];
    if (x.situacao !== 'validada' && x.codigo !== codigoAtual) return x.codigo;
  }
  return null;
}

// Validações do formato antigo (contabilConfig.validacoesBalancete, chave
// `${periodo}||${codigo}`) que podem ser trazidas para contabil_validacoes.
export function validacoesAntigasDoPeriodo(validacoesBalancete, periodo, codigosDoBalancete) {
  const prefixo = `${periodo}||`;
  const existentes = codigosDoBalancete ? new Set(codigosDoBalancete) : null;
  return Object.entries(validacoesBalancete || {})
    .filter(([k, v]) => k.startsWith(prefixo) && v === 'validado')
    .map(([k]) => k.slice(prefixo.length))
    .filter(cod => cod && cod !== CODIGO_A_CLASSIFICAR && (!existentes || existentes.has(cod)))
    .sort((a, b) => a.localeCompare(b, 'pt-BR', { numeric: true }));
}

/**
 * Resumo da etapa Fechar: o que trava e o que só avisa.
 *
 * @param {object} p
 * @param {Array}  p.itens              [{ codigo, situacao }] das contas a conferir
 * @param {number} p.aClassificar       lançamentos do mês com conta 9.9.9
 * @param {number} p.totalDebitos
 * @param {number} p.totalCreditos
 * @param {Array}  p.extratosFaltando   rótulos das contas bancárias sem extrato no mês
 * @param {number} p.qtdLinhas          linhas do balancete do mês
 */
export function resumoFechamento({ itens = [], aClassificar = 0, totalDebitos = 0, totalCreditos = 0, extratosFaltando = [], qtdLinhas = 0 }) {
  const validadas = itens.filter(i => i.situacao === 'validada').length;
  const desatualizadas = itens.filter(i => i.situacao === 'desatualizada').length;
  const pendentes = itens.length - validadas - desatualizadas;
  const diferenca = Math.abs(centavos(totalDebitos) - centavos(totalCreditos)) / 100;

  const bloqueios = [];
  if (qtdLinhas === 0) bloqueios.push({ etapa: 1, chave: 'sem-lancamentos', texto: 'Não há lançamentos nem saldos no período.' });
  if (aClassificar > 0) bloqueios.push({ etapa: 2, chave: 'a-classificar', texto: `${aClassificar} lançamento(s) em A Classificar.` });
  if (pendentes > 0) bloqueios.push({ etapa: 3, chave: 'pendentes', texto: `${pendentes} conta(s) sem validação.` });
  if (desatualizadas > 0) bloqueios.push({ etapa: 3, chave: 'desatualizadas', texto: `${desatualizadas} conta(s) alterada(s) após a validação.` });
  if (diferenca > 0) bloqueios.push({ etapa: 3, chave: 'diferenca', texto: `Total de débitos diferente do total de créditos (diferença de ${diferenca.toLocaleString('pt-BR', { style: 'currency', currency: 'BRL' })}).` });

  const avisos = [];
  if (extratosFaltando.length > 0) {
    avisos.push({ etapa: 1, chave: 'extratos', texto: `Extrato não importado: ${extratosFaltando.join(', ')}.` });
  }

  return {
    contasTotal: itens.length,
    validadas,
    desatualizadas,
    pendentes,
    aClassificar,
    diferenca,
    bloqueios,
    avisos,
    podeFechar: bloqueios.length === 0,
  };
}
